/**
 * One-time registration of the MapLibre ``pmtiles://`` protocol
 * (REQ-UI-148; ADR-016 amendment 2026-08-04).
 *
 * # Why
 *
 * An air-gapped deployment may stage an operator-supplied PMTiles
 * vector-basemap archive that the UI nginx serves same-origin at
 * ``/basemap/tiles.pmtiles`` with HTTP byte-range support (ADR-027 §1).
 * The deployment advertises it through the ordinary tile descriptor as
 * ``type: "vector"`` with ``url_template: "pmtiles:///basemap/tiles.
 * pmtiles"`` — the ``pmtiles://`` scheme is the transport signal,
 * exactly parallel to the existing absolute-vs-relative signaling
 * (REQ-UI-033). MapLibre has no native PMTiles reader; the ``pmtiles``
 * library's :class:`Protocol` supplies one as a custom protocol handler:
 * it reads the archive header/directories via ranged same-origin
 * ``fetch`` calls, synthesizes the TileJSON MapLibre expects from a
 * vector source ``url``, and serves individual tiles from byte ranges.
 *
 * Same-origin means the fetches ride the CSP's ``connect-src 'self'``
 * (REQ-UI-097) and are not external calls under REQ-UI-035 — a
 * deployment configured this way makes zero external browser calls.
 *
 * # Why a dedicated leaf module
 *
 * Registration is a process-global MapLibre side effect. Housing it in a
 * leaf module with an idempotence guard (mirroring the
 * ``registerGlyphIcons`` pattern in :file:`glyphs.ts`) keeps the call
 * site in :func:`useMap`'s mount effect declarative — every mount calls
 * it, exactly one registration happens — and keeps ``style-vector.ts``
 * free of import-time side effects (tests import the style factory
 * constantly; none of them want a protocol registered).
 *
 * Registration is unconditional at map mount rather than gated on the
 * selected descriptor: the guard makes it free, and gating would leave a
 * hole where a Profile switch to a pmtiles descriptor after mount races
 * the registration.
 *
 * Cross-references:
 *   - REQ-UI-148 (same-origin PMTiles basemap); REQ-UI-033/-035/-097.
 *   - ADR-016 amendment 2026-08-04; ADR-027 §1 (nginx serving).
 *   - map/use-map.ts (the mount-effect call site).
 *   - map/style-vector.ts (consumes the descriptor URL verbatim).
 */
import { addProtocol } from "maplibre-gl";
import { Protocol } from "pmtiles";

/**
 * Module-level idempotence guard. MapLibre's protocol registry is
 * process-global; registering twice is harmless but noisy, and the
 * guard gives tests a single well-defined registration to assert on.
 */
let registered = false;

/**
 * Register the ``pmtiles://`` protocol with MapLibre (idempotent).
 *
 * Called from :func:`useMap`'s mount effect **before** the
 * :class:`maplibre-gl.Map` is constructed — MapLibre resolves a style's
 * source URLs during construction, so a late registration would lose
 * the very first style load of a ``pmtiles://`` source.
 *
 * The :class:`pmtiles.Protocol` instance is shared across all maps for
 * the life of the page; it maintains a small per-archive header/
 * directory cache, so repeated tile requests do not re-fetch archive
 * metadata.
 */
export function registerPmtilesProtocol(): void {
  if (registered) {
    return;
  }
  registered = true;
  const protocol = new Protocol();
  addProtocol("pmtiles", protocol.tile);
}
